import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { modelingApi } from '@/api';
import { useModelingListQuery } from '@/hook';
import { VFlex, VInput, VSelect, VStack, VTextarea } from '@/package';
import { useModelVersionTrainingFormContext } from '@/provider';
import type { Modeling } from '@/type';

const VersionSelectModel = () => {
  const { t } = useTranslation();
  const {
    formState: { touchedFields },
    isVersionNameError,
    clearErrors,
    register,
    setError,
    setValue,
    watch,
  } = useModelVersionTrainingFormContext();
  const { modelingId } = useParams();
  const [versionName, setVersionName] = useState<string | undefined>(undefined);

  const { data } = useModelingListQuery({
    modelID: modelingId,
    versionAll: true,
  });

  useEffect(() => {
    if (!versionName) return;

    const timerId = setTimeout(async () => {
      const res = await modelingApi.getCount({
        version: versionName,
      });

      if (res.data.resultData === 0) {
        setValue('modelVersion', versionName);
      } else {
        setError('modelVersion', {
          type: 'duplicated',
          message: t(
            'modeling.modelNameIsExistsErrorMessage.denominationalist',
          ),
        });
      }
    }, 200);

    return () => clearTimeout(timerId);
  }, [versionName]);

  const formVersionName = versionName ?? watch('modelVersion');

  return (
    <VStack gap={1} className="w-full">
      <VFlex gap={1}>
        <VSelect
          fluid
          label={t('modeling.modelNameLabel.demonist')}
          required
          value={watch('sensorType')?.id ?? ''}
          onChange={e => {
            const modeling = (data ?? []).find(
              m => m.id === e._target.value,
            ) as Modeling;
            setValue('sensorType', modeling);
            setValue('modelName', modeling.name);
          }}
          placeholder={t('modeling.selectModelPlaceholder.derision')}
          itemList={
            data?.map(m => ({
              value: m.id,
              label: `${m.versionType === 'VersionModel' ? m.version : m.name} ${m.version ? `(v-${m.version})` : ''}`,
            })) ?? []
          }
        />

        <VInput
          fluid
          label={t('modeling.modelEpochLabel.bimane')}
          {...register('epoch')}
          onChange={e => {
            if (isNaN(Number(e.target.value))) {
              return;
            }
            setValue('epoch', Number(e.target.value));
          }}
          // readOnly
          onBlur={e => {
            const value = Number(e.target.value);

            if (isNaN(value) || e.target.value === '') {
              setValue('epoch', 4);
              return;
            }

            if (value < 4) {
              setValue('epoch', 4);
            } else if (value > 100) {
              setValue('epoch', 100);
            }
          }}
        />
      </VFlex>

      <VInput
        {...register('modelVersion')}
        label={'version name'}
        fluid
        required
        pattern="([A-Za-z0-9_\.\-])+"
        placeholder={t('modeling.versionName.alacreatine')}
        value={formVersionName}
        onChange={e => {
          setVersionName(e.target.value);

          if (!e.target.checkValidity()) {
            setError('modelVersion', {
              type: 'pattern',
              message: t('modeling.modelNameValidationErrorMessage.bargeman'),
            });
          } else {
            clearErrors('modelVersion');
          }
        }}
        valid={
          !touchedFields.modelVersion ||
          (touchedFields.modelVersion && !isVersionNameError)
        }
        comment={isVersionNameError ? isVersionNameError.message : ''}
      />

      <VTextarea
        label={t('modeling.modelDescriptionLabel.badigeon')}
        placeholder={t('modeling.modelDescriptionPlaceholder.rhizocarpian')}
        value={watch('modelDescription')}
        onChange={e => setValue('modelDescription', e.target.value)}
        fluid
      />
    </VStack>
  );
};

export default VersionSelectModel;
